import { useQuery } from '@tanstack/react-query';
import { GameCache, type CacheEntry } from '@replay/gamecache';

let cachePromise: Promise<GameCache> | null = null;

/** One GameCache per tab. Game files only — saves live in a separate store. */
export function getCache() {
  cachePromise ??= GameCache.open();
  return cachePromise;
}

export function useCacheEntry(sha256: string | undefined | null) {
  return useQuery({
    queryKey: ['cache-entry', sha256],
    queryFn: async (): Promise<CacheEntry | null> => (sha256 ? (await getCache()).get(sha256) : null),
    enabled: !!sha256,
    staleTime: 0,
  });
}

export function useCacheList() {
  return useQuery({
    queryKey: ['cache-list'],
    queryFn: async () => {
      const c = await getCache();
      const [entries, usage] = await Promise.all([c.list(), c.usage()]);
      return { entries: entries.sort((a, b) => b.lastUsedAt - a.lastUsedAt), usage };
    },
  });
}

export interface CachePolicy {
  maxBytes: number | null;
  maxAgeDays: number | null;
}
const POLICY_KEY = 'replay.cachePolicy';
export function getCachePolicy(): CachePolicy {
  try {
    return { maxBytes: null, maxAgeDays: 60, ...JSON.parse(localStorage.getItem(POLICY_KEY) ?? '{}') };
  } catch {
    return { maxBytes: null, maxAgeDays: 60 };
  }
}
export function setCachePolicy(p: CachePolicy) {
  try {
    localStorage.setItem(POLICY_KEY, JSON.stringify(p));
  } catch {
    /* storage unavailable */
  }
}

/** Apply the user's automatic cleanup policy (pinned games are always kept). */
export async function runAutoClean() {
  const p = getCachePolicy();
  if (!p.maxBytes && !p.maxAgeDays) return;
  const c = await getCache();
  await c.autoClean({ maxBytes: p.maxBytes ?? undefined, maxAgeDays: p.maxAgeDays ?? undefined });
}
